import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Arrow2 from '@/components/Icon/Arrow2';
import cls from 'classnames';
import { traceEvent } from '@wk/wk-gatherer';
import useGTM from '@/hooks/useGtag';

interface IAccordionItem {
  question: string;
  answer: string;
  id: number;
}

interface QuestionsProps {
  questionsAnswers: IAccordionItem[];
  title: Omit<IAccordionItem, 'id'>;
  className?: string;
  exposeEvent?: string;
}

interface IAccordionItemCom extends IAccordionItem {
  index: number;
  handleGA: (gaName?: string, extra?: { [key: string]: any }) => void;
  isLastChild: boolean;
  isFirstChild: boolean;
}

const arrowCss = 'h-3 w-3 xl:h-5 xl:w-5';

const AccordionItem = ({ question, answer, index, handleGA, isFirstChild }: IAccordionItemCom) => {
  const [isActive, setIsActive] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleAccordion = () => {
    setIsActive(!isActive);
    handleGA('Click', {
      question_number: index,
      question_name: question,
    });
  };

  return (
    <div className="relative transition-all duration-500">
      <div
        onClick={toggleAccordion}
        className={cls('cursor-pointer', isFirstChild ? '' : 'mobile:pt-[16px] pc:pt-[32px]')}
      >
        <div className="flex items-center justify-between">
          <h3 className="pr-3 font-semibold mobile:text-[14px] pc:text-[20px]">{question}</h3>
          <span className={`transform transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`}>
            <Arrow2 className={cls(arrowCss)} />
          </span>
        </div>
      </div>
      <div
        ref={contentRef}
        style={{ maxHeight: isActive ? `${contentRef?.current?.scrollHeight || 0}px` : '0' }}
        className="relative overflow-hidden text-xs leading-5 transition-all duration-300 lg:text-sm lg:leading-6 xl:text-base xl:leading-6"
      >
        <div
          className={cls(
            'text-wk-words-3 mobile:pt-[12px] mobile:text-[12px] mobile:leading-[20px] pc:pt-[16px] pc:text-[16px] pc:leading-[24px] [&_a]:text-wk-blue-400 [&_a]:underline [&_a]:decoration-wk-blue-400 [&_a]:underline-offset-2 [&_strong]:font-semibold',
          )}
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      </div>
    </div>
  );
};

const Questions: React.FC<QuestionsProps> = ({ questionsAnswers, className, exposeEvent, title }) => {
  const { t, i18n } = useTranslation('resources');
  const sendDataToGTM = useGTM();
  const router = useRouter();
  const currentPath = router.pathname;
  const [key, setKey] = useState<number>(0);
  const handleGA = (gaName?: string, extra: { [key: string]: any } = {}) => {
    // 获取当前路径
    const gaNameFix = `O_FaqModule_${gaName}`;
    sendDataToGTM(gaNameFix);
    traceEvent({
      data: {
        _event: gaNameFix,
        ...extra,
        // 点击的所有页面名称
        page_name: currentPath,
      },
    });
  };

  // 切换语言后重新渲染 避免文案更新高度不一致
  useEffect(() => {
    if (!router.isReady) return;
    setKey(key + 1);
  }, [i18n.language]);

  return (
    <section className={cls(className)} data-expose-event={exposeEvent}>
      <div key={key} className="bg-[#F7F8FF] mobile:rounded-[24px] mobile:p-[20px] pc:rounded-[36px] pc:p-[48px]">
        {title?.question && (
          <div
            className={cls('relative transition-all duration-500 ', {
              'mb-[80px] mobile:mb-[32px]': !!questionsAnswers?.length,
            })}
          >
            <div className="cursor-pointer  mobile:pb-[12px]  pc:pb-[24px]">
              <div className="flex items-center justify-between">
                <h3 className="mobile::text-[18px] pr-3 font-black	pc:text-[24px]">{title.question}</h3>
              </div>
            </div>
            <div className="relative overflow-hidden text-xs leading-5 transition-all duration-500 lg:text-sm lg:leading-6 xl:text-base xl:leading-6">
              <div
                className="not-italic text-wk-words-3 mobile:text-[12px] mobile:leading-[20px] pc:text-[16px] pc:leading-[24px] [&_a]:text-wk-blue-400 [&_a]:underline [&_a]:decoration-wk-blue-400 [&_a]:underline-offset-2 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: title.answer }}
              />
            </div>
          </div>
        )}
        {!!questionsAnswers?.length && (
          <>
            <h3 className="pr-3 font-black mobile:mb-[16px] mobile:text-[18px] pc:mb-[32px]	pc:text-[24px]">
              {t('常见问题')}
            </h3>
            {questionsAnswers.map((qa, index) => (
              <div
                key={qa.id}
                className={cls(
                  'border-b border-wk-border-1 [&:last-child]:border-b-0',
                  questionsAnswers.length - 1 === index ? '' : 'mobile:pb-[16px] pc:pb-[32px]',
                )}
              >
                <AccordionItem
                  id={qa.id}
                  question={qa.question}
                  answer={qa.answer}
                  handleGA={handleGA}
                  index={index + 1}
                  isFirstChild={index === 0}
                  isLastChild={index === questionsAnswers.length - 1}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
};

export default Questions;
